/**
 * 根据问卷答案生成推荐配置（全部在前端计算，不上传任何密钥）
 */

import type { StepId } from './wizardSteps'

export interface WizardAnswers {
  device?: string
  channel?: string
  goal?: string
  model?: string
}

export interface RecommendedConfig {
  deployMethod: string
  deployDetail: string
  baseSkills: { name: string; desc: string }[]
  advancedSkills: { name: string; desc: string }[]
  oneLiner: string
  dockerHint?: string
}

export function buildRecommendation(answers: Record<StepId, string>): RecommendedConfig {
  const device = answers.device || ''
  const channel = answers.channel || ''
  const goal = answers.goal || ''

  const isMac = device === 'pc-mac'
  const isVps = device === 'vps'

  let deployMethod: string
  let deployDetail: string
  let dockerHint: string | undefined

  if (device === 'hosted') {
    deployMethod = '一键托管 / 镜像'
    deployDetail = '推荐使用 Hostinger 或 腾讯云 Lighthouse 的一键 Docker 镜像部署，无需自己配环境。'
    dockerHint = '选择带 OpenClaw 的镜像，按说明填写 API Key 与通道配置即可。'
  } else if (isVps) {
    deployMethod = 'VPS + Docker'
    deployDetail = '在云服务器上使用 Docker Compose 部署，记得配置 shm_size: \'2gb\' 与开放 18789 端口。'
    dockerHint = '文档见「部署专区」→ Docker 进阶。'
  } else if (isMac) {
    deployMethod = 'macOS 本地'
    deployDetail = '推荐 Node.js 22+ 环境，执行 npm install -g openclaw 后运行 openclaw onboard 完成初始化。'
  } else if (device === 'pc-windows') {
    deployMethod = 'Windows 本地'
    deployDetail = '建议先配置 WSL2，在 WSL 内按 Linux 方式安装；或使用 Docker Desktop 运行。'
    dockerHint = '详见「安全与排障」中的 WSL2 配置指引。'
  } else {
    deployMethod = '本地 / 自选'
    deployDetail = '根据你的系统选择「部署专区」中的对应教程。'
  }

  const baseSkills: { name: string; desc: string }[] = [
    { name: 'GOG (谷歌全家桶)', desc: '搜索、Gmail 等' },
    { name: 'Tavily Search', desc: 'AI 友好搜索引擎' },
    { name: 'Summarize', desc: '长文本总结' },
  ]

  const advancedSkills: { name: string; desc: string }[] = []

  if (goal === 'dev' || goal === 'office') {
    advancedSkills.push(
      { name: 'Composio', desc: '集成 860+ 工具' },
      { name: 'Playwright', desc: '浏览器自动化' }
    )
  }
  if (goal === 'office' || channel === 'feishu') {
    advancedSkills.push({ name: '飞书插件', desc: '企业协同' })
  }
  if (goal === 'kb') {
    advancedSkills.push(
      { name: 'Obsidian Direct', desc: '连接本地知识库' },
      { name: 'Firecrawl MCP', desc: '全网深度抓取与结构化' },
      { name: 'Exa Search', desc: '开发者专用搜索' }
    )
  }
  if (goal === 'smart-home') {
    advancedSkills.push({ name: 'Home Assistant CLI', desc: '局域网控灯、控温' })
  }
  if (goal === 'scrape' || goal === 'dev') {
    advancedSkills.push({ name: 'Playwright', desc: '浏览器自动化' })
  }
  if (!advancedSkills.length) {
    advancedSkills.push(
      { name: 'Composio', desc: '集成 860+ 工具' },
      { name: 'AgentMail', desc: '独立邮箱' }
    )
  }

  let oneLiner = 'npm install -g openclaw && openclaw onboard'
  if (device === 'hosted') {
    oneLiner = '使用托管镜像时，在控制台按指引填写配置，无需本地安装。'
  } else if (isVps) {
    oneLiner = 'docker compose up -d  # 需先准备好 docker-compose.yml（见部署专区）'
  }

  return {
    deployMethod,
    deployDetail,
    baseSkills,
    advancedSkills: [...new Map(advancedSkills.map((s) => [s.name, s])).values()],
    oneLiner,
    dockerHint,
  }
}
